class PredictiveSearch extends HTMLElement {
  constructor() {
    super();
    this.cachedMap = new Map();
    const input = this.input;
    const resetButton = this.resetButton;
    this.focusElement = input;
    this.resultItems = [];
    this.activeResultIndex = -1;
    this.activeRequestUrl = null;

    if (!input || !resetButton) return;

    resetButton.addEventListener('click', this.clear.bind(this));
    input.addEventListener('input', FoxTheme.utils.debounce(this.onChange.bind(this), 300));
    input.addEventListener('focus', this.onFocus.bind(this));
    input.addEventListener('keydown', this.handleKeydown.bind(this));
    this.searchContent = this.querySelector('.search__content');
    this.searchRecommendationEmpty = this.dataset.searchRecommendationEmpty === 'true';
    this.header = document.querySelector('header');

    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    this.searchProductTypes?.addEventListener('change', this.handleProductTypeChange.bind(this));

    this.isHeaderSearch = this.closest('header') !== null;

    this.boundHandleMenuDrawerOpen = this.handleMenuDrawerOpen.bind(this);
    document.addEventListener('menu-drawer:open', this.boundHandleMenuDrawerOpen);

    this.states = {
      OPEN: 'predictive-search-open',
      LOADING: 'btn--loading',
      SEARCH_OPEN: 'search-open',
    };

    this.syncComboboxState();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.boundHandleClickOutside);
    document.removeEventListener('menu-drawer:open', this.boundHandleMenuDrawerOpen);
  }

  get input() {
    return this.querySelector('input[type="search"]');
  }
  get resetButton() {
    return this.querySelector('button[type="reset"]');
  }

  get searchProductTypes() {
    return this.querySelector('#SearchProductTypes');
  }

  get resultsContainer() {
    return this.querySelector('[id^="PredictiveSearchResults-"]');
  }

  syncComboboxState() {
    const resultsContainer = this.resultsContainer;
    if (!this.input || !resultsContainer) return;

    this.input.setAttribute('aria-controls', resultsContainer.id);
    this.input.setAttribute('aria-owns', resultsContainer.id);
    this.input.setAttribute('aria-expanded', 'false');
    this.input.removeAttribute('aria-activedescendant');
  }

  isVisibleAction(element) {
    if (!element || !element.isConnected) return false;
    if (element.matches(':disabled, [aria-disabled="true"]')) return false;
    if (element.closest('[hidden], [aria-hidden="true"]')) return false;

    const style = window.getComputedStyle(element);
    return style.display !== 'none' && style.visibility !== 'hidden' && element.getClientRects().length > 0;
  }

  collectResultItems() {
    const resultsContainer = this.resultsContainer;
    if (!resultsContainer) return [];

    const candidates = [];
    const addCandidate = (option, action) => {
      if (!this.isVisibleAction(option) || !this.isVisibleAction(action)) return;
      candidates.push({ option, action });
    };

    resultsContainer.querySelectorAll('li[role="option"]').forEach((sourceOption) => {
      const action = sourceOption.querySelector('a[href], button:not(:disabled)');
      sourceOption.removeAttribute('role');
      sourceOption.removeAttribute('aria-selected');
      addCandidate(action, action);
    });

    resultsContainer.querySelectorAll('.predictive-search-result--products .product-card').forEach((productCard) => {
      const action = productCard.querySelector('.product-card__title a[href]');
      addCandidate(action, action);
    });

    resultsContainer.querySelectorAll('.search__results-all').forEach((searchAll) => {
      const action = searchAll.querySelector('a[href], button[type="submit"]:not(:disabled)');
      addCandidate(action, action);
    });

    candidates.sort((first, second) => {
      if (first.option === second.option) return 0;
      return first.option.compareDocumentPosition(second.option) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
    });

    const seenDestinations = new Set();
    return candidates.filter(({ option, action }) => {
      const destination = action.matches('a[href]')
        ? new URL(action.href, document.baseURI).href
        : `submit:${action.getAttribute('form') || action.form?.id || 'predictive-search'}`;
      if (seenDestinations.has(destination)) return false;
      seenDestinations.add(destination);

      const index = seenDestinations.size;
      option.id = `${resultsContainer.id}-option-${index}`;
      option.setAttribute('role', 'option');
      option.setAttribute('aria-selected', 'false');
      option.classList.remove('focused');
      return true;
    });
  }

  clearActiveResult() {
    this.resultItems.forEach(({ option }) => {
      option.classList.remove('focused');
      option.setAttribute('aria-selected', 'false');
    });
    this.activeResultIndex = -1;
    this.input?.removeAttribute('aria-activedescendant');
  }

  currentResultItems() {
    const itemsAreCurrent =
      this.resultItems.length > 0 &&
      this.resultItems.every(({ option, action }) => this.isVisibleAction(option) && this.isVisibleAction(action));

    if (!itemsAreCurrent) {
      this.clearActiveResult();
      this.resultItems = this.collectResultItems();
    }

    return this.resultItems;
  }

  setActiveResult(index) {
    const resultItems = this.currentResultItems();
    if (!resultItems.length || !this.input) return;

    this.clearActiveResult();
    const normalizedIndex = (index + resultItems.length) % resultItems.length;
    const activeResult = resultItems[normalizedIndex];
    this.activeResultIndex = normalizedIndex;
    activeResult.option.classList.add('focused');
    activeResult.option.setAttribute('aria-selected', 'true');
    this.input.setAttribute('aria-activedescendant', activeResult.option.id);
    activeResult.option.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    this.input.focus({ preventScroll: true });
  }

  handleKeydown(event) {
    if (event.isComposing || event.keyCode === 229) return;

    if (event.key === 'Escape') {
      if (!this.classList.contains(this.states.OPEN)) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();
      this.toggleSearchState(false);
      this.input?.focus({ preventScroll: true });
      return;
    }

    const hasModifier = event.altKey || event.ctrlKey || event.metaKey || event.shiftKey;
    if (hasModifier) return;

    if (event.key === 'Enter') {
      const activeResult = this.currentResultItems()[this.activeResultIndex];
      if (!activeResult) return;
      event.preventDefault();
      activeResult.action.click();
      return;
    }

    if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
    const resultItems = this.currentResultItems();
    if (!this.classList.contains(this.states.OPEN) || !resultItems.length) return;

    event.preventDefault();
    const direction = event.key === 'ArrowDown' ? 1 : -1;
    const nextIndex = this.activeResultIndex === -1
      ? event.key === 'ArrowDown' ? 0 : resultItems.length - 1
      : this.activeResultIndex + direction;
    this.setActiveResult(nextIndex);
  }

  handleMenuDrawerOpen() {
    this.toggleSearchState(false);
  }

  onFocus(event) {
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
    if (this.header) {
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom)}px`
      );
    }
    if (this.isHeaderSearch && !this.searchRecommendationEmpty) {
      document.body.classList.add('search-open');
    }

    if (!this.searchRecommendationEmpty) {
      this.searchContent?.classList.remove('hidden');
    }

    this.classList.add('predictive-search-open');
    this.input.setAttribute('aria-expanded', 'false');
    if (this.getQuery().length === 0) {
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url, event);
  }

  getQuery() {
    return this.input?.value.trim() || '';
  }

  clear(event = null) {
    event?.preventDefault();
    if (!this.input) return;
    this.activeRequestUrl = null;
    this.input.value = '';
    this.input.focus();
    this.removeAttribute('results');
    this.toggleSearchState(false);
  }

  handleProductTypeChange(evt) {
    const query = this.getQuery();
    if (query.length > 0) {
      const url = this.setupURL().toString();
      this.renderSection(url);
    }
  }

  setupURL() {
    const url = new URL(`${FoxTheme.routes.shop_url}${FoxTheme.routes.predictive_search_url}`);
    let search_term = this.getQuery();
    if (this.searchProductTypes && this.searchProductTypes.value != '') {
      search_term = `product_type:${this.searchProductTypes.value} AND ${encodeURIComponent(search_term)}`;
    }
    return (
      url.searchParams.set('q', search_term),
      url.searchParams.set('resources[limit]', this.dataset.resultsLimit || 3),
      url.searchParams.set('resources[limit_scope]', 'each'),
      url.searchParams.set('section_id', FoxTheme.utils.getSectionId(this)),
      url
    );
  }

  onChange() {
    this.clearActiveResult();
    if (this.getQuery().length === 0) {
      this.clear();
      return;
    }
    const url = this.setupURL().toString();
    this.renderSection(url);
  }

  renderSection(url) {
    this.toggleSearchState(false);
    this.activeRequestUrl = url;
    this.cachedMap.has(url) ? this.renderSectionFromCache(url) : this.renderSectionFromFetch(url);
  }

  renderSectionFromCache(url) {
    const responseText = this.cachedMap.get(url);
    this.renderSearchResults(responseText), this.setAttribute('results', '');
  }

  renderSectionFromFetch(url) {
    this.setLoadingState(true);

    fetch(url)
      .then((response) => {
        if (!response.ok) throw new Error('Network response was not ok');
        return response.text();
      })
      .then((responseText) => {
        this.cachedMap.set(url, responseText);
        if (this.activeRequestUrl === url) {
          this.renderSearchResults(responseText);
        }
      })
      .catch((error) => {
        console.error('Error fetching data: ', error);
        this.setAttribute('error', 'Failed to load data');
      })
      .finally(() => {
        if (this.activeRequestUrl === url) {
          this.setLoadingState(false);
        }
      });
  }
  renderSearchResults(responseText) {
    const id = 'PredictiveSearchResults-' + FoxTheme.utils.getSectionId(this);
    const targetElement = this.resultsContainer;

    if (!targetElement || targetElement.id !== id) return;

    const parser = new DOMParser();
    const parsedDoc = parser.parseFromString(responseText, 'text/html');
    const contentElement = parsedDoc.getElementById(id);
    if (!contentElement) return;

    this.clearActiveResult();
    this.searchContent?.classList.remove('hidden');
    targetElement.innerHTML = contentElement.innerHTML;
    this.toggleSearchState(true);
    this.resultItems = this.collectResultItems();
  }

  handleClickOutside(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const shouldClose = this.isHeaderSearch
      ? !this.contains(target) &&
        ((target.classList.contains('fixed-overlay') && target.closest('.header-section')) ||
          target.closest('.header__search-close'))
      : !this.contains(target);

    if (shouldClose) {
      setTimeout(() => this.toggleSearchState(false));
    }
  }

  toggleSearchState(isOpen) {
    this.classList.toggle(this.states.OPEN, isOpen);
    if (this.isHeaderSearch) {
      document.body.classList.toggle(this.states.SEARCH_OPEN, isOpen);
    }
    this.input?.setAttribute('aria-expanded', String(isOpen && this.getQuery().length > 0));
    if (!isOpen) {
      this.clearActiveResult();
    }
    if (!isOpen && this.searchRecommendationEmpty) {
      this.searchContent?.classList.add('hidden');
    }
  }

  setLoadingState(isLoading) {
    if (isLoading) {
      this.setAttribute('loading', 'true');
      this.resetButton?.classList.add(this.states.LOADING);
    } else {
      this.removeAttribute('loading');
      this.resetButton?.classList.remove(this.states.LOADING);
      this.setAttribute('results', 'true');
    }
  }
}
customElements.define('predictive-search', PredictiveSearch);
